import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import {ChartSettings} from 'entities/chart';
import {FormikT} from 'utils/types';
import i18n from 'i18n';

interface Props {
    chartSettingsFormik: FormikT<ChartSettings>;
    boxedDisplay?: boolean;
    showTitleRequiredError?: boolean;
}

export interface ChartSettingsTitleEditorRef {
    flushPendingUpdate: () => void;
}

const DEBOUNCE_MS = 150;

const ChartSettingsTitleEditorComponent = forwardRef<ChartSettingsTitleEditorRef, Props>(({
    chartSettingsFormik,
    boxedDisplay = false,
    showTitleRequiredError = false,
}, ref) => {
    const [editing, setEditing] = useState(false);
    const currentTitle = chartSettingsFormik.values.title || '';
    const currentDescription = chartSettingsFormik.values.description || '';
    const [localTitle, setLocalTitle] = useState(currentTitle);
    const [localDescription, setLocalDescription] = useState(currentDescription);
    const titleDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const descriptionDebounceRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!editing) {
            setLocalTitle(currentTitle);
            setLocalDescription(currentDescription);
        }
    }, [currentTitle, currentDescription, editing]);

    useEffect(() => {
        if (showTitleRequiredError) {
            setEditing(true);
        }
    }, [showTitleRequiredError]);

    useEffect(() => () => {
        if (titleDebounceRef.current) {
            clearTimeout(titleDebounceRef.current);
        }
        if (descriptionDebounceRef.current) {
            clearTimeout(descriptionDebounceRef.current);
        }
    }, []);

    const syncTitleToFormik = useCallback((value: string) => {
        if (value !== chartSettingsFormik.values.title) {
            chartSettingsFormik.setFieldValue('title', value, false);
        }
    }, [chartSettingsFormik]);

    const syncDescriptionToFormik = useCallback((value: string) => {
        if (value !== chartSettingsFormik.values.description) {
            chartSettingsFormik.setFieldValue('description', value, false);
        }
    }, [chartSettingsFormik]);

    const flushPendingUpdate = useCallback(() => {
        if (titleDebounceRef.current) {
            clearTimeout(titleDebounceRef.current);
            titleDebounceRef.current = null;
        }
        if (descriptionDebounceRef.current) {
            clearTimeout(descriptionDebounceRef.current);
            descriptionDebounceRef.current = null;
        }
        syncTitleToFormik(localTitle);
        syncDescriptionToFormik(localDescription);
    }, [localTitle, localDescription, syncTitleToFormik, syncDescriptionToFormik]);

    useImperativeHandle(ref, () => ({
        flushPendingUpdate,
    }), [flushPendingUpdate]);

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setLocalTitle(value);

        if (titleDebounceRef.current) {
            clearTimeout(titleDebounceRef.current);
        }

        titleDebounceRef.current = setTimeout(() => {
            syncTitleToFormik(value);
        }, DEBOUNCE_MS);
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setLocalDescription(value);

        if (descriptionDebounceRef.current) {
            clearTimeout(descriptionDebounceRef.current);
        }

        descriptionDebounceRef.current = setTimeout(() => {
            syncDescriptionToFormik(value);
        }, DEBOUNCE_MS);
    };

    const handleTitleBlockKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setEditing(true);
        }
    };

    const hasTitle = (localTitle || '').trim().length > 0;
    const displayTitle = hasTitle ? localTitle : i18n.t('chart_settings_form_placeholder_name.text');

    if (editing) {
        return (
            <div className="chart-settings-preview-card__editor">
                <div className="chart-settings-preview-card__editor-fields">
                    <input
                        type="text"
                        className={
                            'chart-settings-preview-card__title-input' +
                            (showTitleRequiredError ? ' chart-settings-preview-card__title-input--invalid' : '')
                        }
                        value={localTitle}
                        onChange={handleTitleChange}
                        onBlur={flushPendingUpdate}
                        placeholder={i18n.t('chart_settings_form_placeholder_name.text')}
                        maxLength={255}
                        autoFocus
                    />
                    {showTitleRequiredError ? (
                        <div className="chart-settings-preview-card__title-error">
                            {i18n.t('chart_settings_add_name_to_save.text')}
                        </div>
                    ) : null}
                    <input
                        type="text"
                        className="chart-settings-preview-card__description-input"
                        value={localDescription}
                        onChange={handleDescriptionChange}
                        onBlur={flushPendingUpdate}
                        placeholder={i18n.t('chart_settings_add_description_optional.text')}
                        maxLength={512}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className={
                'chart-settings-preview-card__title-block chart-settings-preview-card__title-block--clickable' +
                (boxedDisplay ? ' chart-settings-preview-card__title-block--boxed' : '')
            }
            onClick={() => setEditing(true)}
            onKeyDown={handleTitleBlockKeyDown}
            role="button"
            tabIndex={0}
        >
            <div className={`chart-settings-preview-card__title${hasTitle ? '' : ' chart-settings-preview-card__title--empty'}`}>
                {displayTitle}
            </div>
            {localDescription ? (
                <div className="chart-settings-preview-card__subtitle">
                    {localDescription}
                </div>
            ) : (
                <div className="chart-settings-preview-card__subtitle chart-settings-preview-card__subtitle--empty">
                    {i18n.t('chart_settings_add_description.text')}
                </div>
            )}
        </div>
    );
});

ChartSettingsTitleEditorComponent.displayName = 'ChartSettingsTitleEditor';

export const ChartSettingsTitleEditor = React.memo(ChartSettingsTitleEditorComponent);
